/**
 * Card Reconciliation — lists every CARD-tagged payment for a day so
 * the cashier can tick them off against the card-machine batch slip.
 * Refunds show negative, split payments are flagged in yellow.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CreditCard, RefreshCw, Download, Check, AlertTriangle, Split } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtR = (v) => "R " + (Number(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return iso; }
};

const todayISO = () => new Date().toISOString().split("T")[0];

const STORAGE = "card_recon_ticks_v1";

export default function CardReconciliation() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [ticks, setTicks] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/card-reconciliation`, { params: { date } });
      setData(res.data);
      try {
        const stored = JSON.parse(localStorage.getItem(`${STORAGE}:${date}`) || "{}");
        setTicks(stored);
      } catch { setTicks({}); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const toggleTick = (id) => {
    setTicks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(`${STORAGE}:${date}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const rows = data?.rows || [];
  const total = data?.total_card || 0;
  const tickedTotal = rows.filter((r) => ticks[r.sale_id]).reduce((s, r) => s + r.signed_amount, 0);
  const remaining = total - tickedTotal;

  const exportCsv = () => {
    if (!rows.length) return;
    const csv = [
      ["Ticked", "Time", "Sale #", "Cashier", "Customer", "Transaction Type", "Card Amount", "Signed", "Sale Total", "Split?", "Other Methods"].join(","),
      ...rows.map((r) => [
        ticks[r.sale_id] ? "Y" : "",
        fmtTime(r.created_at),
        r.transaction_number || "",
        r.cashier || "",
        (r.customer_name || "").replace(/,/g, ";"),
        r.transaction_type || "",
        r.card_amount.toFixed(2),
        r.signed_amount.toFixed(2),
        r.sale_total.toFixed(2),
        r.is_split ? "Y" : "",
        (r.other_methods || []).map((o) => `${o.method}:${o.amount}`).join("; "),
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `card-reconciliation-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10" data-testid="card-recon-page">
      <div className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-black flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-400" /> Card Reconciliation
            </h1>
            <p className="text-xs text-slate-300 mt-0.5">Match each system card entry against your card-machine batch slip</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        <Card>
          <CardContent className="pt-4 flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="card-recon-date" className="w-44" />
            </div>
            <Button onClick={load} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Load"}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!rows.length}>
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </CardContent>
        </Card>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardHeader className="pb-2"><CardDescription>System Card Total</CardDescription><CardTitle className="text-2xl text-emerald-700">{fmtR(total)}</CardTitle></CardHeader></Card>
          <Card><CardHeader className="pb-2"><CardDescription>Card Entries</CardDescription><CardTitle className="text-2xl">{rows.length}</CardTitle></CardHeader></Card>
          <Card><CardHeader className="pb-2"><CardDescription>Ticked (matched)</CardDescription><CardTitle className="text-2xl text-blue-700">{fmtR(tickedTotal)}</CardTitle></CardHeader></Card>
          <Card className={remaining !== 0 ? "border-amber-400 bg-amber-50" : ""}>
            <CardHeader className="pb-2"><CardDescription>Unticked</CardDescription>
              <CardTitle className={`text-2xl ${remaining !== 0 ? "text-amber-800" : "text-gray-800"}`}>{fmtR(remaining)}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
          <strong>How to use:</strong> grab your card-machine batch slip. Click ✓ next to each system entry that appears on the slip. Rows still un-ticked at the bottom are your <strong>phantom entries</strong> — the ones the system counted but the card machine never took.
          <div className="mt-1.5 text-xs text-blue-800">
            <strong>Blue &ldquo;ACCOUNT PAYMENT&rdquo; rows</strong> are customers paying down their store account on the card machine. These count towards the daily card total but aren&apos;t sales — they&apos;ll still be on your batch slip so tick them off normally.
          </div>
        </div>

        {/* Rows */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Card Payments ({rows.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-emerald-600" /></div>
            ) : !rows.length ? (
              <p className="py-8 text-center text-gray-500">No card payments for this day.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b-2 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="text-center py-2 w-10">Tick</th>
                      <th className="text-left py-2">Time</th>
                      <th className="text-left py-2">Sale #</th>
                      <th className="text-left py-2">Cashier</th>
                      <th className="text-left py-2">Customer</th>
                      <th className="text-left py-2">Type</th>
                      <th className="text-right py-2">Card R</th>
                      <th className="text-left py-2">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const ticked = !!ticks[r.sale_id];
                      return (
                        <tr key={r.sale_id} className={`border-b hover:bg-gray-50 ${ticked ? "bg-emerald-50" : ""}`}>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => toggleTick(r.sale_id)}
                              className={`h-6 w-6 rounded border-2 flex items-center justify-center transition ${
                                ticked ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-300 hover:border-emerald-400"
                              }`}
                              data-testid={`tick-${r.transaction_number}`}
                              title="Mark as matched on batch slip"
                            >
                              {ticked && <Check className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="py-2 font-mono text-xs">{fmtTime(r.created_at)}</td>
                          <td className="py-2 font-mono text-xs">{r.transaction_number}</td>
                          <td className="py-2">{r.cashier || "—"}</td>
                          <td className="py-2 truncate max-w-[180px]">{r.customer_name}</td>
                          <td className="py-2">
                            <Badge variant="outline" className={
                              r.is_refund ? "border-rose-300 text-rose-700" :
                              r.source === "account_payment" ? "border-blue-300 text-blue-700" :
                              "border-gray-300"
                            }>
                              {r.transaction_type}
                            </Badge>
                          </td>
                          <td className={`py-2 text-right font-bold ${r.is_refund ? "text-rose-700" : "text-emerald-700"}`}>
                            {r.is_refund && "− "}{fmtR(r.card_amount)}
                          </td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              {r.source === "sale_legacy" && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" title="No payments[] array — using legacy sale.payment_method field. Cashup counts these but recon needed a fallback branch to find them.">
                                  <AlertTriangle className="h-3 w-3 mr-0.5" /> Legacy
                                </Badge>
                              )}
                              {r.is_split && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" title={r.other_methods.map((o) => `${o.method}: ${fmtR(o.amount)}`).join(", ")}>
                                  <Split className="h-3 w-3 mr-0.5" /> Split
                                </Badge>
                              )}
                              {r.card_amount !== r.sale_total && !r.is_refund && !r.is_split && (
                                <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]" title={`Card R${r.card_amount} but sale total R${r.sale_total}`}>
                                  <AlertTriangle className="h-3 w-3 mr-0.5" /> Mismatch
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
